import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
} from '@tanstack/react-query';
import { useApiClient } from '../api/api-context.js';
import { isNetworkError } from '../api/errors.js';
import type { QueueableMethod, QueuedWrite } from './db.js';
import { useOffline } from './offline-context.js';

/**
 * A mutation that survives the connection dropping.
 *
 * The shop is mid-booking, the signal goes. The write is written to IndexedDB instead of the
 * network, the UI says so plainly, and it is delivered — in the order it was made, behind
 * everything queued before it — the moment the connection returns.
 *
 * Not every write should use this. A write is queueable only when applying it later still
 * makes sense: recording a payment, advancing a stage, editing a customer. Anything whose
 * result the user must see NOW (a login, a Razorpay checkout, a stock reservation) should be a
 * plain `useMutation` and fail loudly instead.
 */

export interface QueueableRequest {
  method: QueueableMethod;
  path: string;
  body?: unknown;
  /** i18n key naming this write in the pending list. */
  labelKey: string;
  labelParams?: Record<string, string | number>;
  /** Query keys to invalidate once the write lands, whether now or after a flush. */
  invalidate?: string[][];
}

/** Either the server answered, or the write is safely on disk waiting for the connection. */
export type QueueableMutationResult<TData> =
  { queued: false; data: TData } | { queued: true; write: QueuedWrite };

export interface UseQueueableMutationOptions<TData, TVariables> extends Omit<
  UseMutationOptions<QueueableMutationResult<TData>, unknown, TVariables>,
  'mutationFn'
> {
  /** Turn the caller's variables into the HTTP request to make (or queue). */
  buildRequest: (variables: TVariables) => QueueableRequest;
}

function newIdempotencyKey(): string {
  const cryptoRef = globalThis.crypto as Crypto | undefined;
  if (cryptoRef && typeof cryptoRef.randomUUID === 'function') return cryptoRef.randomUUID();
  return `w_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

export function useQueueableMutation<TData = unknown, TVariables = void>(
  options: UseQueueableMutationOptions<TData, TVariables>,
): UseMutationResult<QueueableMutationResult<TData>, unknown, TVariables> {
  const { buildRequest, ...mutationOptions } = options;
  const client = useApiClient();
  const { isOffline, enqueue } = useOffline();

  return useMutation<QueueableMutationResult<TData>, unknown, TVariables>({
    ...mutationOptions,
    mutationFn: async (variables: TVariables) => {
      const request = buildRequest(variables);
      // The same key on the direct attempt and on any later replay, so a write that reached
      // the server just before the connection died cannot be applied a second time.
      const idempotencyKey = newIdempotencyKey();

      if (isOffline) {
        return { queued: true, write: await enqueue({ ...request, id: idempotencyKey }) };
      }

      try {
        const data = await client.request<TData>({
          method: request.method,
          path: request.path,
          body: request.body,
          headers: { 'Idempotency-Key': idempotencyKey },
        });
        return { queued: false, data };
      } catch (error) {
        if (isNetworkError(error)) {
          // It never got there. Fall back to the queue rather than losing the shop's work.
          return { queued: true, write: await enqueue({ ...request, id: idempotencyKey }) };
        }
        throw error;
      }
    },
  });
}
